import { getFixedT } from 'i18next';
import { CXPaymentBlock } from '~/components/ClientExperience/OneFlow/models/cx-payment-block.model';
import { PaymentBlock } from '~/models/blocks/payment-block.model';
import { PaymentStatus } from '~/types/model.types';
import { AutoPayStatus } from '~/types/server.types';
import { formatDate } from '~/utils/date.utils';
import { NAMESPACES } from '~/i18n/namespaces';
import { TFuncKey } from 'react-i18next';
import { ITooltipProps } from '@honeybook/hbui/dist/components/HBTooltip/HBTooltip';

export const AUTOPAY_DISPLAY_STATUS: Record<AutoPayStatus, string> = {
    active: 'On',
    inactive: 'Inactive',
    paused: 'Paused',
    pending_approval: 'Pending'
};

export const getAutoPayTooltipText = (
    paymentBlock: PaymentBlock | CXPaymentBlock,
    autoPayStatus,
    autoPayClientName: string
) => {
    const t = getFixedT(null, NAMESPACES.CLIENT_EXPERIENCE, 'autopay');
    const autoPayStatusText = AUTOPAY_DISPLAY_STATUS[autoPayStatus];
    const memberSide = !!autoPayClientName;

    const TooltipContent = memberSide
        ? [
              {
                  condition:
                      !paymentBlock.isPaid &&
                      autoPayStatus === AutoPayStatus.Paused,
                  title: 'Autopay is ' + autoPayStatusText,
                  description: t('paused_member_message', {
                      clientName: autoPayClientName
                  })
              },
              {
                  condition:
                      !paymentBlock.invoiceBlock.isRecurringInvoice &&
                      paymentBlock.isMilestone &&
                      !paymentBlock.isPaid,
                  title: 'Autopay: ' + autoPayStatusText,
                  description:
                      autoPayClientName +
                      ' will be charged within 24 hours of your payment request.'
              },
              {
                  condition: !paymentBlock.isPaid && !paymentBlock.isOverDue,
                  title: 'Autopay: ' + autoPayStatusText,
                  description:
                      autoPayClientName + ' will be charged on the due date.'
              },
              {
                  condition: paymentBlock.isPaid || paymentBlock.isRefunded,
                  title: 'Paid using Autopay',
                  description:
                      autoPayClientName +
                      ' was last charged on ' +
                      formatDate(paymentBlock.paymentChargeDate, 'MM D, YYYY')
              },
              {
                  condition: paymentBlock.isOverDue,
                  title: 'Autopay: ' + autoPayStatusText,
                  description:
                      'We will try to charge ' +
                      autoPayClientName +
                      ' again in 24 hours.'
              }
          ]
        : [
              // client side
              {
                  condition:
                      !paymentBlock.isPaid &&
                      autoPayStatus === AutoPayStatus.Paused,
                  title: 'Autopay is ' + autoPayStatusText,
                  description: t('paused_client_message')
              },
              {
                  condition:
                      !paymentBlock.isPaid &&
                      !paymentBlock.isMilestone &&
                      !paymentBlock.invoiceBlock.isRecurringInvoice,
                  title: 'Autopay: ' + autoPayStatusText,
                  description: 'You use Autopay for all your payments.'
              },
              {
                  condition:
                      !paymentBlock.isPaid &&
                      !paymentBlock.isMilestone &&
                      paymentBlock.invoiceBlock.isRecurringInvoice,
                  title: 'Autopay: ' + autoPayStatusText,
                  description:
                      'Your next recurring payment will be charged on ' +
                      formatDate(paymentBlock.paymentDueDate, 'MM D, YYYY')
              },
              {
                  condition:
                      !paymentBlock.invoiceBlock.isRecurringInvoice &&
                      paymentBlock.isMilestone &&
                      !paymentBlock.milestoneCompletedOn,
                  title: 'Autopay: ' + autoPayStatusText,
                  description:
                      'You’ll be charged when the next milestone is completed.'
              },
              {
                  condition:
                      !paymentBlock.invoiceBlock.isRecurringInvoice &&
                      paymentBlock.isMilestone &&
                      paymentBlock.milestoneCompletedOn,
                  title: 'Autopay: ' + autoPayStatusText,
                  description:
                      'You’ll be charged within 24 hours of the payment request.'
              },
              {
                  condition: paymentBlock.isPaid || paymentBlock.isRefunded,
                  title: 'Paid using Autopay',
                  description:
                      'Last charged on: ' +
                      formatDate(paymentBlock.paymentChargeDate, 'MM D, YYYY')
              },
              {
                  condition: paymentBlock.isOverDue,
                  title: 'Autopay: ' + autoPayStatusText,
                  description: 'We will try to charge you again in 24 hours.'
              }
          ];

    return TooltipContent.find(content => content.condition);
};

export const getPaymentStatusTooltipData = (paymentBlock: PaymentBlock) => {
    const { isPaid, isAch, isCreditCard, paymentStatus } = paymentBlock;
    if (
        paymentBlock.isConsideredMemberPaid ||
        paymentStatus === PaymentStatus.PartiallyRefunded ||
        paymentStatus === PaymentStatus.Refunded
    ) {
        return null;
    }

    const tooltipTextOptions: Array<{
        condition: boolean;
        text: TFuncKey<typeof NAMESPACES.BUILDER>;
        tooltipProps: Omit<ITooltipProps, 'target'>;
    }> = [
        {
            condition: isPaid && paymentBlock.shouldShowFixBankAccount,
            text: 'responses.payments.go_to_bank_details_tooltip',
            tooltipProps: {
                maxWidth: '263px'
            }
        },
        {
            condition: paymentBlock.paymentStatus === PaymentStatus.Refunding,
            text: 'responses.payments.refund_in_proccess_tooltip',
            tooltipProps: {
                maxWidth: '247px'
            }
        },
        {
            condition: isAch && paymentBlock.isStripeDepositDate,
            text: 'responses.payments.transfer_approved_tooltip',
            tooltipProps: {
                maxWidth: '244px'
            }
        },
        {
            condition: isAch && paymentBlock.isInstantDepositOn,
            text: 'responses.payments.instant_deposit_tooltip',
            tooltipProps: {
                maxWidth: '263px'
            }
        },
        {
            condition: isCreditCard,
            text: 'responses.payments.credit_card_tooltip',
            tooltipProps: {
                maxWidth: '205px'
            }
        },
        {
            condition: isAch,
            text: 'responses.payments.bank_transfer_tooltip',
            tooltipProps: {
                maxWidth: '184px'
            }
        }
    ];

    return tooltipTextOptions.find(content => content.condition);
};
